import { useContext } from "react";
import type { NextRouter } from "next/router";
import { RouterContext } from "next/dist/shared/lib/router-context.shared-runtime";

// How the sign-in form reaches the page after a verified code.
//
// The sixth digit fires the verify request and, at the same moment, asks the
// Pages Router to prefetch the destination: Next loads that page's code ahead
// of the answer. The destination's data (the dashboard's getServerSideProps)
// needs the session cookie the verify response sets, so it cannot be fetched
// before the answer; a Next prefetch of a getServerSideProps page fetches code
// only. On the dashboard wall the destination is the page already showing, so
// its code is present and the prefetch costs nothing.
//
// A correct code then moves to the destination through the router instead of
// a full page load: Next fetches the page's data with the fresh session and
// renders it in place, so the wall's scripts, styles, and fonts are not
// downloaded and run again. A wrong code changes nothing: the prefetch left no
// state to drop.

/**
 * The Pages Router when this surface renders inside a Next.js page, null
 * otherwise (a test rendering the form on its own). next/router's useRouter
 * throws in that second case.
 */
export function useRouterIfMounted(): NextRouter | null {
  return useContext(RouterContext);
}

/** Loads the destination page's code now, so it is not fetched after the verify response. */
export function prefetchSignInDestination(
  router: Pick<NextRouter, "prefetch"> | null,
  href: string
) {
  if (!router) return;
  // Next's prefetch is a no-op outside production builds and can reject for
  // an external or unknown destination; either way the sign-in goes on.
  void router.prefetch(href).catch(() => undefined);
}

/**
 * Replaces the sign-in page with the destination without a full page load.
 * The router resolves false when it abandoned the change (another navigation
 * won, or Next fell back to a full load of its own); a full load then makes
 * sure the member still lands on the destination.
 */
export async function replaceWithSignInDestination(
  router: Pick<NextRouter, "replace"> | null,
  location: Pick<Location, "replace">,
  href: string
) {
  if (router) {
    try {
      if (await router.replace(href)) return;
    } catch {
      // A route the client bundle cannot load: fall back to a full load.
    }
  }
  location.replace(href);
}
